import * as Sentry from "@sentry/react";
import { Suspense, useCallback, useEffect, useRef } from "react";
import { Redirect, Route, Switch, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import "@/lib/apideck-vault-preload";
import { BootstrapDegradedBanner } from "@/components/bootstrap-degraded-banner";
import { BottomNav } from "@/components/bottom-nav";
import { CommandPalette } from "@/components/command-palette";
import { EntityMigrationBanner } from "@/components/entity-migration-banner";
import { EntityScopeBanner } from "@/components/entity-scope-banner";
import { ErrorBoundary } from "@/components/error-boundary";
import { GatedRoute } from "@/components/gated-route";
import { HeaderNav } from "@/components/header-nav";
import { OctaLogo } from "@/components/octa-logo";
import { SupportModeBanner } from "@/components/support-mode-banner";
import { SyncBanner } from "@/components/sync-banner";
import { TrialBanner } from "@/components/trial-banner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UpgradeModal } from "@/components/upgrade-modal";
import { UpgradeModalProvider } from "@/hooks/use-upgrade-modal";
import i18n from "@/i18n";
import {
  captureAndRegisterUtms,
  incrementUserProfile,
  registerSuperProperties,
  resetSessionModules,
  setUserProfileOnce,
  setUserProfileProperties,
  trackEvent,
  trackPageView,
  wsLocalKey,
} from "@/lib/analytics";
import { AuthProvider, useAuth } from "@/lib/auth";
import { EntityProvider } from "@/lib/entity-context";
import { FeaturesProvider } from "@/lib/features";
import { lazyRetry } from "@/lib/lazy-retry";
import { prefetchLikelyRoutes } from "@/lib/route-prefetch";
import { SupportModeProvider } from "@/lib/support-mode";
import { SyncActivityProvider } from "@/lib/sync-activity-context";
import { PROVIDER_REPORT_SLUGS } from "@shared/provider-reports";
import type { Workspace } from "@shared/schema";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

const NotFound = lazyRetry(() => import("@/pages/not-found"));
const AuthPage = lazyRetry(() => import("@/pages/auth-page"));
const Dashboard = lazyRetry(() => import("@/pages/dashboard"));
const APPage = lazyRetry(() => import("@/pages/ap/index"));
const BillFormPage = lazyRetry(() => import("@/pages/ap/bill-form"));
const BillNewPage = lazyRetry(() => import("@/pages/ap/bill-new"));
const BillCsvUploadPage = lazyRetry(() => import("@/pages/ap/bill-csv-upload"));
const BillSyncPage = lazyRetry(() => import("@/pages/ap/bill-sync"));
const BillScanUploadPage = lazyRetry(() => import("@/pages/ap/bill-scan-upload"));
const BillDetailPage = lazyRetry(() => import("@/pages/ap/bill-detail"));
const InboundEinvoicesPage = lazyRetry(() => import("@/pages/ap/inbound-einvoices"));
const InboundEinvoiceDetailPage = lazyRetry(() => import("@/pages/ap/inbound-einvoice-detail"));
const PoDetailPage = lazyRetry(() => import("@/pages/ap/po-detail"));
const ARPage = lazyRetry(() => import("@/pages/ar/index"));
const InvoiceFormPage = lazyRetry(() => import("@/pages/ar/invoice-form"));
const InvoiceNewPage = lazyRetry(() => import("@/pages/ar/invoice-new"));
const InvoiceCsvUploadPage = lazyRetry(() => import("@/pages/ar/invoice-csv-upload"));
const InvoiceBulkConvertPage = lazyRetry(() => import("@/pages/ar/invoice-bulk-convert"));
const EinvoiceBulkUploadPage = lazyRetry(() => import("@/pages/ar/einvoice-bulk-upload"));
const InvoiceSyncPage = lazyRetry(() => import("@/pages/ar/invoice-sync"));
const InvoiceDetailPage = lazyRetry(() => import("@/pages/ar/invoice-detail"));
const VendorDetailPage = lazyRetry(() => import("@/pages/ap/vendor-detail"));
const VendorFormPage = lazyRetry(() => import("@/pages/ap/vendor-form"));
const CustomerDetailPage = lazyRetry(() => import("@/pages/ar/customer-detail"));
const CustomerFormPage = lazyRetry(() => import("@/pages/ar/customer-form"));
const FinancingPage = lazyRetry(() => import("@/pages/financing/index"));
const ApplicationDetailPage = lazyRetry(() => import("@/pages/financing/application-detail"));
const FacilityDetailPage = lazyRetry(() => import("@/pages/financing/facility-detail"));
const SettingsPage = lazyRetry(() => import("@/pages/settings/layout"));
const TasksPage = lazyRetry(() => import("@/pages/tasks/index"));
const TaskFormPage = lazyRetry(() => import("@/pages/tasks/task-form"));
const ReportsPage = lazyRetry(() => import("@/pages/reports/index"));
const ReportDetailPage = lazyRetry(() => import("@/pages/reports/report-detail"));
const ProviderReportDetailPage = lazyRetry(() => import("@/pages/reports/provider-report-detail"));
const BudgetsPage = lazyRetry(() => import("@/pages/reports/budgets"));
const BudgetVsActualPage = lazyRetry(() => import("@/pages/reports/budget-vs-actual"));
const SchedulesPage = lazyRetry(() => import("@/pages/reports/schedules"));
const ReportBuilderPage = lazyRetry(() => import("@/pages/reports/report-builder"));
const CustomerPortalPage = lazyRetry(() => import("@/pages/customer-portal"));
const VendorPortalPage = lazyRetry(() => import("@/pages/vendor-portal"));
const PayRedirect = lazyRetry(() => import("@/pages/pay-redirect"));
// OCT-11350 — public receipt page behind the payment-received email's receipt link.
const ReceiptDownloadPage = lazyRetry(() => import("@/pages/receipt-download"));
const CheckoutRedirect = lazyRetry(() => import("@/pages/checkout-redirect"));
const AutomationBuilderPage = lazyRetry(() => import("@/pages/automations/builder"));
const AutomationsListPage = lazyRetry(() => import("@/pages/automations/index"));
const AutomationRunsPage = lazyRetry(() => import("@/pages/automations/runs"));
const AutomationHealthPage = lazyRetry(() => import("@/pages/automations/health"));
const AutomationApprovalsPage = lazyRetry(() => import("@/pages/automations/approvals"));
const InboxPage = lazyRetry(() => import("@/pages/inbox/index"));
const OnboardingPage = lazyRetry(() => import("@/pages/onboarding"));
const ChoosePlanPage = lazyRetry(() => import("@/pages/choose-plan"));
const CreditNotesPage = lazyRetry(() => import("@/pages/ar/credit-notes"));
const CreditNoteDetailPage = lazyRetry(() => import("@/pages/ar/credit-note-detail"));
const DealDetailPage = lazyRetry(() => import("@/pages/ar/deal-detail"));
const ContractDetailPage = lazyRetry(() => import("@/pages/ar/contract-workspace"));
const ContractRecordDetailPage = lazyRetry(() => import("@/pages/ar/contract-record-detail"));
const InvoiceDraftQueuePage = lazyRetry(() => import("@/pages/ar/invoice-draft-queue"));
const AdminPage = lazyRetry(() => import("@/pages/admin"));
const OperationsPage = lazyRetry(() => import("@/pages/operations/index"));
const GrnDetailPage = lazyRetry(() => import("@/pages/ap/grn-detail"));
const ChatPage = lazyRetry(() => import("@/pages/chat/index"));
const CommunicationsPage = lazyRetry(() => import("@/pages/communications/index"));
const ActivatePage = lazyRetry(() => import("@/pages/activate"));
const UpgradeWelcomePage = lazyRetry(() => import("@/pages/upgrade-welcome"));
const DowngradeNoticePage = lazyRetry(() => import("@/pages/downgrade-notice"));
const SystemHealthPage = lazyRetry(() => import("@/pages/system-health"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <OctaLogo animate />
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    </div>
  );
}

/**
 * Name Flow's connect-flow auth popup opens itself as
 * (`AUTH_POPUP_NAME` in octaflow's connect-widget-modal.tsx) — `window.name`
 * survives cross-origin navigation (unlike `window.opener`, which the
 * Auth0/Clerk hop severs via COOP), so it is the only reliable signal this
 * window IS that popup.
 */
const CONNECT_AUTH_POPUP_WINDOW_NAME = "octa-connect-auth";

/**
 * The connect-flow auth popup's only legitimate landing page is the static
 * `connect/authed` self-closer (never reached by React). If it ends up here
 * instead — e.g. a lost `returnTo` stranding it on an authenticated SPA
 * route — showing the user's real dashboard (or, for a superadmin, the
 * `SuperAdminShell` redirect to `/admin`) in what's supposed to be an
 * invisible popup is actively misleading (OCT-6509). Close it instead of
 * rendering anything.
 */
function ConnectAuthPopupGuard() {
  useEffect(() => {
    window.close();
  }, []);
  return <FullScreenLoader />;
}

function SyncRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation("/settings?section=integrations&sub=sync", { replace: true });
  }, [setLocation]);
  return null;
}

function RemindersRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation("/ar?tab=reminders", { replace: true });
  }, [setLocation]);
  return null;
}

function _OperationsRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation("/", { replace: true });
  }, [setLocation]);
  return null;
}

function usePageTracking() {
  const [location] = useLocation();
  const prevLocation = useRef(location);
  useEffect(() => {
    if (location !== prevLocation.current) {
      prevLocation.current = location;
      trackPageView();
    }
  }, [location]);
  useEffect(() => {
    trackPageView();
  }, []);
}

function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={Dashboard} />
        <Route path="/ap">{() => <GatedRoute feature="ap" Component={APPage} />}</Route>
        <Route path="/ap/bills/new/manual">
          {() => <GatedRoute feature="ap" Component={BillFormPage} writerOnly="/ap?tab=bills" />}
        </Route>
        <Route path="/ap/bills/new/csv">
          {() => <GatedRoute feature="ap" Component={BillCsvUploadPage} writerOnly="/ap?tab=bills" />}
        </Route>
        <Route path="/ap/bills/new/sync">
          {() => <GatedRoute feature="ap" Component={BillSyncPage} writerOnly="/ap?tab=bills" />}
        </Route>
        <Route path="/ap/bills/new/scan">
          {() => <GatedRoute feature="ap" Component={BillScanUploadPage} writerOnly="/ap?tab=bills" />}
        </Route>
        <Route path="/ap/bills/new">
          {() => <GatedRoute feature="ap" Component={BillNewPage} writerOnly="/ap?tab=bills" />}
        </Route>
        <Route path="/ap/bills/:id/edit">
          {(params) => (
            <GatedRoute feature="ap" Component={BillFormPage} params={params} writerOnly={`/ap/bills/${params.id}`} />
          )}
        </Route>
        <Route path="/ap/vendors/new">
          {() => <GatedRoute feature="ap" Component={VendorFormPage} writerOnly="/ap?tab=vendors" />}
        </Route>
        <Route path="/ap/vendors/:id">
          {(params) => <GatedRoute feature="ap" Component={VendorDetailPage} params={params} />}
        </Route>
        <Route path="/ap/grns/:id">
          {(params) => <GatedRoute feature="ap" Component={GrnDetailPage} params={params} />}
        </Route>
        <Route path="/ap/purchase-orders/:id">
          {(params) => <GatedRoute feature="purchaseOrders" Component={PoDetailPage} params={params} />}
        </Route>
        <Route path="/ap/bills/:id">
          {(params) => <GatedRoute feature="ap" Component={BillDetailPage} params={params} />}
        </Route>
        <Route path="/ap/inbound-einvoices/:id">
          {(params) => <GatedRoute feature="ap" Component={InboundEinvoiceDetailPage} params={params} />}
        </Route>
        <Route path="/ap/inbound-einvoices">{() => <GatedRoute feature="ap" Component={InboundEinvoicesPage} />}</Route>
        <Route path="/ap/bills">{() => <Redirect to="/ap?tab=bills" />}</Route>
        <Route path="/ap/vendors">{() => <Redirect to="/ap?tab=vendors" />}</Route>
        <Route path="/ap/purchase-orders">{() => <Redirect to="/ap?tab=purchase-orders" />}</Route>
        <Route path="/ap/grns">{() => <Redirect to="/ap?tab=goods-received" />}</Route>
        <Route path="/ar">{() => <GatedRoute feature="ar" Component={ARPage} />}</Route>
        <Route path="/ar/customers/new">
          {() => <GatedRoute feature="ar" Component={CustomerFormPage} writerOnly="/ar?tab=customers" />}
        </Route>
        <Route path="/ar/invoices/new/manual">
          {() => <GatedRoute feature="ar" Component={InvoiceFormPage} writerOnly="/ar?tab=invoices" />}
        </Route>
        <Route path="/ar/invoices/new/csv">
          {() => <GatedRoute feature="ar" Component={InvoiceCsvUploadPage} writerOnly="/ar?tab=invoices" />}
        </Route>
        <Route path="/ar/invoices/new/einvoice-xlsx">
          {() => <GatedRoute feature="ar" Component={EinvoiceBulkUploadPage} writerOnly="/ar?tab=invoices" />}
        </Route>
        <Route path="/ar/invoices/new/sync">
          {() => <GatedRoute feature="ar" Component={InvoiceSyncPage} writerOnly="/ar?tab=invoices" />}
        </Route>
        <Route path="/ar/invoices/bulk-convert">
          {() => <GatedRoute feature="ar" Component={InvoiceBulkConvertPage} writerOnly="/ar?tab=invoices" />}
        </Route>
        <Route path="/ar/invoices/new">
          {() => <GatedRoute feature="ar" Component={InvoiceNewPage} writerOnly="/ar?tab=invoices" />}
        </Route>
        <Route path="/ar/invoices/:id/edit">
          {(params) => (
            <GatedRoute
              feature="ar"
              Component={InvoiceFormPage}
              params={params}
              writerOnly={`/ar/invoices/${params.id}`}
            />
          )}
        </Route>
        <Route path="/ar/customers/:id">
          {(params) => <GatedRoute feature="ar" Component={CustomerDetailPage} params={params} />}
        </Route>
        <Route path="/ar/invoices/:id">
          {(params) => <GatedRoute feature="ar" Component={InvoiceDetailPage} params={params} />}
        </Route>
        <Route path="/ar/deals/:id">
          {(params) => <GatedRoute feature="deals" Component={DealDetailPage} params={params} />}
        </Route>
        {
          /* Compliance Dashboard was retired in Task #1200. Per-submission
            audit detail moved to the invoice detail's `EInvoiceTransmissionPanel`,
            and the workspace-wide success-rate KPI moved inline to the AR →
            E-Invoicing tab. Both legacy routes redirect to the new tab while
            preserving any existing query string so bookmarks keep working. */
        }
        <Route path="/ar/compliance">
          {() => {
            const search = typeof window !== "undefined" ? window.location.search : "";
            const params = new URLSearchParams(search);
            params.set("tab", "einvoicing");
            return <Redirect to={`/ar?${params.toString()}`} />;
          }}
        </Route>
        <Route path="/ar/compliance-dashboard">
          {() => {
            const search = typeof window !== "undefined" ? window.location.search : "";
            const params = new URLSearchParams(search);
            params.set("tab", "einvoicing");
            return <Redirect to={`/ar?${params.toString()}`} />;
          }}
        </Route>
        <Route path="/ar/credit-notes/:id">
          {(params) => <GatedRoute feature="arInstruments" Component={CreditNoteDetailPage} params={params} />}
        </Route>
        <Route path="/ar/credit-notes">
          {() => <GatedRoute feature="arInstruments" Component={CreditNotesPage} />}
        </Route>
        <Route path="/ar/contracts/records/:id">
          {(params) => <GatedRoute feature="contractInvoicing" Component={ContractRecordDetailPage} params={params} />}
        </Route>
        <Route path="/ar/contracts/records">{() => <Redirect to="/ar?tab=contracts" />}</Route>
        <Route path="/ar/contracts/:id">
          {(params) => <GatedRoute feature="contractToCash" Component={ContractDetailPage} params={params} />}
        </Route>
        <Route path="/ar/invoice-drafts">
          {() => <GatedRoute feature="contractToCash" Component={InvoiceDraftQueuePage} />}
        </Route>
        <Route path="/ar/contracts">{() => <Redirect to="/ar?tab=contracts" />}</Route>
        <Route path="/ar/invoices">
          {() => {
            const search = typeof window !== "undefined" ? window.location.search : "";
            const params = new URLSearchParams(search);
            params.set("tab", "invoices");
            return <Redirect to={`/ar?${params.toString()}`} />;
          }}
        </Route>
        <Route path="/ar/customers">{() => <Redirect to="/ar?tab=customers" />}</Route>
        <Route path="/ar/deals">{() => <Redirect to="/ar?tab=deals" />}</Route>
        <Route path="/financing">{() => <GatedRoute feature="financing" Component={FinancingPage} />}</Route>
        <Route path="/financing/applications/:id">
          {(params) => <GatedRoute feature="financing" Component={ApplicationDetailPage} params={params} />}
        </Route>
        <Route path="/financing/facilities/:id">
          {(params) => <GatedRoute feature="financing" Component={FacilityDetailPage} params={params} />}
        </Route>
        <Route path="/financing/applications">{() => <Redirect to="/financing?tab=applications" />}</Route>
        <Route path="/financing/facilities">{() => <Redirect to="/financing?tab=facilities" />}</Route>
        <Route path="/financing/kyb">{() => <Redirect to="/financing?tab=kyb" />}</Route>
        <Route path="/operations" component={OperationsPage} />
        <Route path="/reconciliation">{() => <Redirect to="/operations?tab=reconciliation" />}</Route>
        <Route path="/inbox" component={InboxPage} />
        <Route path="/automations/approvals">
          {() => <GatedRoute feature="automations" Component={AutomationApprovalsPage} />}
        </Route>
        <Route path="/automations/runs">
          {() => <GatedRoute feature="automations" Component={AutomationRunsPage} />}
        </Route>
        <Route path="/automations/health">
          {() => <GatedRoute feature="automations" Component={AutomationHealthPage} />}
        </Route>
        <Route path="/automations/:id">
          {(params) => <GatedRoute feature="automations" Component={AutomationBuilderPage} params={params} />}
        </Route>
        <Route path="/automations">{() => <GatedRoute feature="automations" Component={AutomationsListPage} />}</Route>
        <Route path="/tasks/new">
          {() => <GatedRoute feature="tasks" Component={TaskFormPage} writerOnly="/tasks" />}
        </Route>
        <Route path="/reminders" component={RemindersRedirect} />
        <Route path="/tasks">{() => <GatedRoute feature="tasks" Component={TasksPage} />}</Route>
        <Route path="/reports">{() => <GatedRoute feature="aiReports" Component={ReportsPage} />}</Route>
        <Route path="/reports/budgets">{() => <GatedRoute feature="aiReports" Component={BudgetsPage} />}</Route>
        <Route path="/reports/budget-vs-actual">
          {() => <GatedRoute feature="aiReports" Component={BudgetVsActualPage} />}
        </Route>
        <Route path="/reports/schedules">{() => <GatedRoute feature="aiReports" Component={SchedulesPage} />}</Route>
        <Route path="/reports/builder">{() => <GatedRoute feature="aiReports" Component={ReportBuilderPage} />}</Route>
        {/* Literal provider-report routes MUST precede the `/reports/:slug` catch-all below or they never match. */}
        {/* These paths carry no wouter `:param`, so the slug is passed explicitly rather than from the (empty) route match. */}
        <Route path={`/reports/${PROVIDER_REPORT_SLUGS.trial_balance}`}>
          {() => (
            <GatedRoute
              feature="aiReports"
              Component={ProviderReportDetailPage}
              params={{ slug: PROVIDER_REPORT_SLUGS.trial_balance }}
            />
          )}
        </Route>
        <Route path={`/reports/${PROVIDER_REPORT_SLUGS.profit_and_loss}`}>
          {() => (
            <GatedRoute
              feature="aiReports"
              Component={ProviderReportDetailPage}
              params={{ slug: PROVIDER_REPORT_SLUGS.profit_and_loss }}
            />
          )}
        </Route>
        <Route path={`/reports/${PROVIDER_REPORT_SLUGS.balance_sheet}`}>
          {() => (
            <GatedRoute
              feature="aiReports"
              Component={ProviderReportDetailPage}
              params={{ slug: PROVIDER_REPORT_SLUGS.balance_sheet }}
            />
          )}
        </Route>
        <Route path="/reports/:slug">
          {(params) => <GatedRoute feature="aiReports" Component={ReportDetailPage} params={params} />}
        </Route>
        <Route path="/settings" component={SettingsPage} />
        <Route path="/system-health" component={SystemHealthPage} />
        <Route path="/choose-plan" component={ChoosePlanPage} />
        <Route path="/sync" component={SyncRedirect} />
        <Route path="/communications">
          {() => <GatedRoute feature="communications" Component={CommunicationsPage} />}
        </Route>
        <Route path="/chat">{() => <GatedRoute feature="aiChat" Component={ChatPage} />}</Route>
        <Route path="/admin" component={AdminPage} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function AuthenticatedApp(
  { bootstrapDegraded = false, bootstrapRetryPending = false, onRetryBootstrap }: {
    bootstrapDegraded?: boolean;
    bootstrapRetryPending?: boolean;
    onRetryBootstrap?: () => void;
  } = {},
) {
  const { user } = useAuth();
  useEffect(() => {
    prefetchLikelyRoutes();
  }, []);
  return (
    <SupportModeProvider>
      <EntityProvider>
        <FeaturesProvider>
          <SyncActivityProvider>
            <UpgradeModalProvider>
              <div className="h-screen flex flex-col bg-background text-foreground overflow-x-hidden">
                <SupportModeBanner />
                <TrialBanner />
                <SyncBanner />
                <HeaderNav />
                {
                  /* OCT-3366: migration banner for entities 2..N of multi-
                    entity workspaces. The component decides whether to
                    render (server status + dismissal); mounting it once
                    here gives every authenticated page the banner without
                    each page needing to opt in. */
                }
                <div className="px-4 sm:px-6 pt-2">
                  {
                    /* OCT-10873: entity-scoped reads fail closed, so a failed
                      `/api/entities` (or a workspace with no active entity)
                      renders an app whose every list is empty and stays empty
                      on `staleTime: Infinity`. This says so, and offers the
                      retry that drops those empty caches. */
                  }
                  {
                    /* OCT-11301: a bootstrap read (workspace / feature flags)
                      that errored leaves the shell running on default flags.
                      Say so, and offer a retry scoped to just those two reads,
                      instead of silently rendering a limited-looking app. */
                  }
                  {bootstrapDegraded && onRetryBootstrap && (
                    <BootstrapDegradedBanner
                      onRetry={onRetryBootstrap}
                      retryPending={bootstrapRetryPending}
                    />
                  )}
                  <EntityScopeBanner />
                  <EntityMigrationBanner userId={user?.id ?? null} />
                </div>
                <main className="flex-1 overflow-auto overflow-x-hidden min-h-0 pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
                  {
                    /* Route-level boundary: a render error (incl. React's
                      "Maximum update depth exceeded" from any runaway render
                      loop) is contained to the content area instead of
                      propagating to the app-root boundary and blanking the
                      whole screen. The chrome (header/nav) stays usable and the
                      user gets Retry/Home without losing the session to a
                      manual page refresh. */
                  }
                  <ErrorBoundary>
                    <AppRouter />
                  </ErrorBoundary>
                </main>
                <BottomNav />
                <CommandPalette />
              </div>
              <UpgradeModal />
            </UpgradeModalProvider>
          </SyncActivityProvider>
        </FeaturesProvider>
      </EntityProvider>
    </SupportModeProvider>
  );
}

function OnboardingGate() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const {
    data: workspace,
    isFetched: wsFetched,
    isFetching: wsFetching,
    refetch: refetchWorkspace,
  } = useQuery<Workspace>({ queryKey: ["/api/workspace"] });
  const {
    data: featuresData,
    isFetched: featFetched,
    isFetching: featFetching,
    refetch: refetchFeatures,
  } = useQuery<import("@/lib/features").FeatureFlags>({
    queryKey: ["/api/workspace/features"],
  });
  const { data: billingData } = useQuery<{ trialEndsAt: string | null; }>({ queryKey: ["/api/billing/status"] });

  const inSupportMode = !!user?.supportMode;

  // OCT-11301 - three states, not two.
  //
  // `bootstrapUnresolved` is the only state that earns a full-screen loader:
  // a read that has not answered even once, with data OR with an error.
  //
  // It is deliberately `isFetched` and NOT `isLoading` / `isPending`. For a
  // query that has never succeeded, TanStack resets `status` to `"pending"`
  // and `failureCount` to `0` at the START of every refetch - so both of those
  // signals go BACKWARDS, and `FeaturesProvider` refetches this exact key the
  // moment it mounts. That made the gate oscillate: features errored ->
  // `planChosen` read as false -> the branch below mounted `FeaturesProvider`
  // -> its refetch flipped the gate back to "still loading" -> the gate
  // swapped to the loader and unmounted the branch -> the fetch errored ->
  // repeat. Measured in this worktree at ~350 requests/second against
  // `/api/workspace/features`. That self-inflicted storm is what kept the
  // per-user bucket exhausted, and it is why a hard refresh never recovered:
  // the client was re-draining the bucket faster than the window could clear.
  // `isFetched` is monotonic per query (`dataUpdateCount || errorUpdateCount`,
  // neither of which a refetch resets), so the loader is shown once and never
  // returns.
  //
  // `featuresUnknown` / `workspaceUnknown` is the state the old
  // `featuresData?.planChosen ?? false` collapsed into a negative answer. A
  // failed fetch is not evidence that a paying, fully-onboarded workspace has
  // no plan, and it must never route the user into pre-onboarding or away from
  // the page they were on. Unknown means: leave the app as it is, tell the
  // user, and let the retry (or the scheduled throttle recovery in
  // `queryClient.ts`) resolve it.
  const bootstrapUnresolved = !wsFetched || !featFetched;
  const featuresUnknown = !bootstrapUnresolved && featuresData === undefined;
  const workspaceUnknown = !bootstrapUnresolved && workspace === undefined;
  const bootstrapDegraded = featuresUnknown || workspaceUnknown;
  const planChosen = featuresData?.planChosen ?? false;

  const retryBootstrap = useCallback(() => {
    void refetchWorkspace();
    void refetchFeatures();
  }, [refetchWorkspace, refetchFeatures]);
  const upgradeOnboardingTier = featuresData?.upgradeOnboardingTier;
  const downgradeNotice = featuresData?.downgradeNotice;

  const sessionTrackedRef = useRef(false);
  useEffect(() => {
    if (sessionTrackedRef.current || !featuresData || !user || bootstrapUnresolved) return;
    sessionTrackedRef.current = true;
    resetSessionModules();
    registerSuperProperties({ tier: featuresData.tier });
    try {
      localStorage.setItem("octa-cached-tier", featuresData.tier);
    } catch {}

    const signupKey = wsLocalKey("octa-signup-completed");
    if (user.firstLoginPending && !localStorage.getItem(signupKey)) {
      try {
        localStorage.setItem(signupKey, "1");
      } catch {}
      const utms = captureAndRegisterUtms();
      setUserProfileOnce({
        $created: new Date().toISOString(),
        signup_date: new Date().toISOString(),
      });
      // GA4 recommended conversion: signup_completed
      trackEvent("Signup Completed", {
        signup_method: "auth0",
        referrer: document.referrer || undefined,
        ...utms,
      });
    } else if (!user.firstLoginPending) {
      incrementUserProfile("login_count");
      const loginKey = wsLocalKey("octa-login-count");
      const loginCountRaw = localStorage.getItem(loginKey);
      const loginCount = loginCountRaw ? parseInt(loginCountRaw, 10) + 1 : 1;
      try {
        localStorage.setItem(loginKey, String(loginCount));
      } catch {}
      trackEvent("Login", { login_count: loginCount });
      // GTM dataLayer: emit a `login` event so GTM-managed conversion tags
      // (Facebook Pixel, LinkedIn Insight, etc.) can fire on returning logins.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "login", user_id: user.id });
    }

    incrementUserProfile("session_count");
    const sessionKey = wsLocalKey("octa-session-count");
    const sessionCountRaw = localStorage.getItem(sessionKey);
    const sessionNumber = sessionCountRaw ? parseInt(sessionCountRaw, 10) + 1 : 1;
    try {
      localStorage.setItem(sessionKey, String(sessionNumber));
    } catch {}
    trackEvent("Session Started", { session_number: sessionNumber });
    registerSuperProperties({ last_active_date: new Date().toISOString() });
    setUserProfileProperties({
      current_tier: featuresData.tier,
      subscription_status: featuresData.subscriptionStatus || "unknown",
    });

    const prevStatus = localStorage.getItem(wsLocalKey("octa-prev-subscription-status"));
    const prevTier = localStorage.getItem(wsLocalKey("octa-prev-tier"));
    if (featuresData.subscriptionStatus === "active" && prevStatus && prevStatus !== "active") {
      trackEvent("Subscription Activated", { tier: featuresData.tier });
    }
    if (
      prevStatus === "trialing" && featuresData.tier === "starter" && featuresData.subscriptionStatus !== "trialing"
    ) {
      trackEvent("Trial Expired", { previous_tier: prevTier || "growth" });
    }
    try {
      localStorage.setItem(wsLocalKey("octa-prev-subscription-status"), featuresData.subscriptionStatus || "");
      localStorage.setItem(wsLocalKey("octa-prev-tier"), featuresData.tier || "");
    } catch {}
  }, [featuresData, user, bootstrapUnresolved]);

  useEffect(() => {
    if (!billingData?.trialEndsAt) return;
    setUserProfileProperties({ trial_end_date: billingData.trialEndsAt });
  }, [billingData?.trialEndsAt]);

  useEffect(() => {
    if (inSupportMode) return;
    if (bootstrapUnresolved) return;
    if (workspace?.locale && workspace.locale !== i18n.language) {
      i18n.changeLanguage(workspace.locale);
    }

    // OCT-11301: with the feature flags unknown, every branch below would be
    // deciding where to send the user from a fetch failure. Stay put.
    if (featuresUnknown) return;

    if (!planChosen) return;

    if (downgradeNotice && workspace?.onboardingCompleted) {
      if (location !== "/downgrade-notice") {
        setLocation("/downgrade-notice");
      }
      return;
    }

    if (upgradeOnboardingTier && workspace?.onboardingCompleted) {
      if (location !== "/upgrade-welcome") {
        setLocation("/upgrade-welcome");
      }
      return;
    }

    if (workspace && !workspace.onboardingCompleted) {
      if (location !== "/onboarding") {
        setLocation("/onboarding");
      }
    } else if (workspace && workspace.onboardingCompleted) {
      if (location === "/onboarding" || location === "/upgrade-welcome" || location === "/downgrade-notice") {
        setLocation("/");
      }
    }
  }, [
    workspace,
    bootstrapUnresolved,
    featuresUnknown,
    planChosen,
    upgradeOnboardingTier,
    downgradeNotice,
    location,
    setLocation,
    inSupportMode,
  ]);

  if (inSupportMode) {
    return <AuthenticatedApp />;
  }

  if (bootstrapUnresolved) {
    return <FullScreenLoader />;
  }

  // OCT-3337 follow-up: pre-onboarding screens still fire non-boot
  // mutations (PATCH /api/user/role-profile, POST /api/entities,
  // POST /api/onboarding/*, etc.) that the entity-header validation
  // latch in queryClient.ts gates. EntityProvider is the only thing
  // that releases that latch (via its failure-path for zero-entity
  // workspaces, or its happy-path once the persisted LS value is
  // reconciled). Wrapping each pre-onboarding branch mounts the
  // provider early so the latch releases and these flows can complete.
  // Side benefit: EntityProvider's GET /api/entities triggers the
  // lazy default-entity seed in list-entities.ts for new workspaces,
  // so provisionDefaultBlueprint and the audit logger stop tripping
  // the "workspace lacks a default entity (D23 / OCT-3359)" warning.
  if (!featuresUnknown && !planChosen) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <EntityProvider>
          <FeaturesProvider>
            <ChoosePlanPage />
          </FeaturesProvider>
        </EntityProvider>
      </Suspense>
    );
  }

  if (downgradeNotice && workspace?.onboardingCompleted) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <EntityProvider>
          <FeaturesProvider>
            <DowngradeNoticePage downgradeNotice={downgradeNotice} />
          </FeaturesProvider>
        </EntityProvider>
      </Suspense>
    );
  }

  if (upgradeOnboardingTier && workspace?.onboardingCompleted) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <EntityProvider>
          <FeaturesProvider>
            <UpgradeWelcomePage upgradeTier={upgradeOnboardingTier} />
          </FeaturesProvider>
        </EntityProvider>
      </Suspense>
    );
  }

  if (!featuresUnknown && workspace && !workspace.onboardingCompleted) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <EntityProvider>
          <FeaturesProvider>
            <OnboardingPage />
          </FeaturesProvider>
        </EntityProvider>
      </Suspense>
    );
  }

  return (
    <AuthenticatedApp
      bootstrapDegraded={bootstrapDegraded}
      bootstrapRetryPending={wsFetching || featFetching}
      onRetryBootstrap={retryBootstrap}
    />
  );
}

function SuperAdminShell() {
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (location !== "/admin") {
      setLocation("/admin", { replace: true });
    }
  }, [location, setLocation]);

  return (
    <SupportModeProvider>
      <EntityProvider>
        <FeaturesProvider>
          <div className="h-screen flex flex-col bg-background text-foreground">
            <HeaderNav />
            <main className="flex-1 overflow-auto min-h-0 pb-16 md:pb-0">
              <Suspense fallback={<PageLoader />}>
                <AdminPage />
              </Suspense>
            </main>
            <BottomNav />
          </div>
        </FeaturesProvider>
      </EntityProvider>
    </SupportModeProvider>
  );
}

function AppContent() {
  const { user, isLoading, isAuthCheckThrottled } = useAuth();

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.fullName,
      });
      Sentry.setTag("workspaceId", user.workspaceId);
    } else if (!isLoading) {
      Sentry.setUser(null);
      Sentry.setTag("workspaceId", undefined);
    }
  }, [user, isLoading]);

  if (window.name === CONNECT_AUTH_POPUP_WINDOW_NAME) {
    return <ConnectAuthPopupGuard />;
  }

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (!user) {
    // OCT-10784: a throttled (429) `/api/auth/me` check is not evidence of a
    // confirmed logout — the session may still be perfectly valid. Keep
    // showing a loading state (the query's `refetchInterval` will retry)
    // instead of bouncing to the login page, which previously turned a
    // transient rate-limit into a false "you're logged out" render.
    if (isAuthCheckThrottled) {
      return <FullScreenLoader />;
    }
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <AuthPage />
      </Suspense>
    );
  }

  if (user.role === "superadmin" && !user.supportMode) {
    return <SuperAdminShell />;
  }

  return <OnboardingGate />;
}

function App() {
  usePageTracking();
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Suspense fallback={<FullScreenLoader />}>
            <Switch>
              <Route path="/pay/:id" component={PayRedirect} />
              <Route path="/receipt/:invoiceId" component={ReceiptDownloadPage} />
              <Route path="/portal/:customerId" component={CustomerPortalPage} />
              <Route path="/vendor-portal/:vendorId" component={VendorPortalPage} />
              <Route path="/checkout" component={CheckoutRedirect} />
              <Route path="/activate" component={ActivatePage} />
              <Route>
                <AuthProvider>
                  <AppContent />
                </AuthProvider>
              </Route>
            </Switch>
          </Suspense>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
